import type { QuestionHistoryEntryResponseSchemaType } from '@/types/portal-api.generated'
import { portalApiClient } from '@/platform/http'
import { withAnalyticsTracking } from '@/utilities/analytics/apiAnalytics'

const HISTORY_PATH = '/v3/clients/analytics/question-history'

type HistoryEntryResponse = QuestionHistoryEntryResponseSchemaType
export type AnalyticsQuestionHistoryEntry = Readonly<{
  id: string
  question: string
  askedAt: number
}>

function mapEntries(entries: HistoryEntryResponse[]): AnalyticsQuestionHistoryEntry[] {
  return entries.map((entry) => ({
    id: entry.id,
    question: entry.question,
    askedAt: Date.parse(entry.asked_at),
  }))
}

export async function fetchQuestionHistory(
  signal: AbortSignal,
): Promise<AnalyticsQuestionHistoryEntry[]> {
  return withAnalyticsTracking(
    async () => {
      const { data } = await portalApiClient.axios.get<HistoryEntryResponse[]>(HISTORY_PATH, {
        signal,
      })
      return mapEntries(data)
    },
    { eventName: 'client:analytics:history_load' },
  )
}

export async function saveQuestionHistory(
  question: string,
  signal: AbortSignal,
): Promise<AnalyticsQuestionHistoryEntry[]> {
  return withAnalyticsTracking(
    async () => {
      const { data } = await portalApiClient.axios.post<HistoryEntryResponse[]>(
        HISTORY_PATH,
        { question },
        { signal },
      )
      return mapEntries(data)
    },
    { eventName: 'client:analytics:history_save' },
  )
}

export async function deleteQuestionHistory(
  id: string | undefined,
  signal: AbortSignal,
): Promise<AnalyticsQuestionHistoryEntry[]> {
  return withAnalyticsTracking(
    async () => {
      const { data } = await portalApiClient.axios.delete<HistoryEntryResponse[]>(
        id ? `${HISTORY_PATH}/${encodeURIComponent(id)}` : HISTORY_PATH,
        { signal },
      )
      return mapEntries(data)
    },
    { eventName: id ? 'client:analytics:history_remove' : 'client:analytics:history_clear' },
  )
}
